/* iter230 — Service Catalog (canonical, tiered).
   Admin view of the new NexFlow Master Service Catalog. Each service is ONE record
   with four INDEPENDENT pricing profiles (HSP / Residential / Property Management /
   Commercial). The redesigned item form edits all canonical fields + each profile.
   Backed by /api/admin/service-catalog. */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Search, Pencil, X, Package, ChevronDown, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TIERS = ['hsp', 'residential', 'property_management', 'commercial'];
const TIER_LABELS = { hsp: 'HSP', residential: 'Residential', property_management: 'Property Management', commercial: 'Commercial' };
const METHODS = [
  { value: 'flat', label: 'Exact Price' },
  { value: 'estimate_required', label: 'Estimate Required' },
  { value: 'not_available', label: 'Not Available' },
  { value: 'needs_review', label: 'Needs Review' },
];
const emptyProfile = () => ({ price: null, pricing_method: 'flat', customer_note: '', internal_note: '', active: true });
const emptyForm = () => ({
  sku: '', name: '', category: '', subcategory: '', department: '',
  customer_scope: '', tech_instructions: '', exclusions: '',
  labor_minutes: '', crew_size: '', warranty: '', addons: [], aliases: [],
  pricing_profiles: Object.fromEntries(TIERS.map((t) => [t, emptyProfile()])),
  active: true, archived: false,
});
const priceDisplay = (p) => {
  if (!p) return '—';
  if (p.pricing_method === 'flat') return p.price != null ? `$${Number(p.price).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '$0.00';
  if (p.pricing_method === 'estimate_required') return 'Est. Req.';
  if (p.pricing_method === 'not_available') return 'N/A';
  return 'Review';
};

const ServiceCatalog = () => {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState('category');   // iter233 'category' | 'table'
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');   // '' | 'active' | 'inactive'
  const [openGroups, setOpenGroups] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/service-catalog${q ? `?q=${encodeURIComponent(q)}` : ''}`, { withCredentials: true });
      setItems(data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load catalog'); }
    setLoading(false);
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => setForm({ ...emptyForm(), _new: true });
  const openEdit = (it) => setForm({
    ...emptyForm(), ...it,
    labor_minutes: it.labor_minutes ?? '', crew_size: it.crew_size ?? '',
    pricing_profiles: { ...emptyForm().pricing_profiles, ...(it.pricing_profiles || {}) },
    _new: false, id: it.id,
  });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        labor_minutes: form.labor_minutes === '' ? null : Number(form.labor_minutes),
        crew_size: form.crew_size === '' ? null : Number(form.crew_size),
      };
      delete payload._new; delete payload.id;
      if (form._new) await axios.post(`${API}/admin/service-catalog`, payload, { withCredentials: true });
      else await axios.put(`${API}/admin/service-catalog/${form.id}`, payload, { withCredentials: true });
      toast.success('Saved');
      setForm(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  const setProfile = (tier, patch) => setForm((f) => ({ ...f, pricing_profiles: { ...f.pricing_profiles, [tier]: { ...f.pricing_profiles[tier], ...patch } } }));

  const filtered = useMemo(() => {
    return items.filter((it) => {
      if (categoryFilter && it.category !== categoryFilter) return false;
      if (statusFilter === 'active' && !it.active) return false;
      if (statusFilter === 'inactive' && it.active) return false;
      return true;
    });
  }, [items, categoryFilter, statusFilter]);

  const categoryOptions = useMemo(() => {
    const m = {};
    items.forEach((it) => { const c = it.category || 'Uncategorized'; m[c] = (m[c] || 0) + 1; });
    return Object.keys(m).sort().map((c) => ({ category: c, count: m[c] }));
  }, [items]);

  const grouped = useMemo(() => {
    const m = {};
    filtered.forEach((it) => { const c = it.category || 'Uncategorized'; (m[c] = m[c] || []).push(it); });
    return Object.keys(m).sort().map((c) => ({ category: c, items: m[c] }));
  }, [filtered]);

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="service-catalog-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-white font-['Outfit'] text-3xl font-bold flex items-center gap-2"><Package className="w-7 h-7 text-[#D4AF37]" /> Service Catalog</h1>
          <p className="text-[#A3A3A3] text-sm mt-1">{items.length} service(s) · four independent tier prices each.</p>
        </div>
        <button data-testid="new-service-btn" onClick={openNew} className="inline-flex items-center gap-2 text-sm font-bold px-3 py-2 rounded-lg bg-[#D4AF37] text-black hover:bg-[#c4a032]"><Plus className="w-4 h-4" /> New Service</button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 text-[#A3A3A3] absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="catalog-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search SKU, name, category…" className="w-full bg-[#0A0A0A] border border-[#262626] rounded-lg pl-9 pr-3 py-2 text-white text-sm" />
        </div>
        <select data-testid="catalog-category-filter" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="bg-[#0A0A0A] border border-[#262626] rounded-lg px-3 py-2 text-white text-sm">
          <option value="">All Categories</option>
          {categoryOptions.map((c) => <option key={c.category} value={c.category}>{c.category} ({c.count})</option>)}
        </select>
        <select data-testid="catalog-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-[#0A0A0A] border border-[#262626] rounded-lg px-3 py-2 text-white text-sm">
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <div className="flex rounded-lg border border-[#262626] overflow-hidden">
          <button data-testid="catalog-view-category" onClick={() => setViewMode('category')} className={`px-3 py-2 text-xs font-bold ${viewMode === 'category' ? 'bg-[#D4AF37] text-black' : 'text-[#A3A3A3]'}`}>By Category</button>
          <button data-testid="catalog-view-table" onClick={() => setViewMode('table')} className={`px-3 py-2 text-xs font-bold ${viewMode === 'table' ? 'bg-[#D4AF37] text-black' : 'text-[#A3A3A3]'}`}>Table</button>
        </div>
      </div>

      {viewMode === 'category' && !loading && (
        <div className="space-y-2" data-testid="catalog-category-browse">
          {grouped.length === 0 ? <p className="text-[#A3A3A3] text-sm py-6 text-center">No services match.</p> : grouped.map((g) => {
            const isOpen = openGroups[g.category] ?? true;
            return (
              <div key={g.category} className="rounded-lg border border-[#262626] overflow-hidden">
                <button data-testid={`catalog-group-${g.category}`} onClick={() => setOpenGroups((p) => ({ ...p, [g.category]: !isOpen }))}
                  className="w-full flex items-center justify-between px-4 py-2.5 bg-[#141414] hover:bg-[#1a1a1a]">
                  <span className="text-white font-bold flex items-center gap-2">{isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}{g.category}</span>
                  <span className="text-[#A3A3A3] text-xs">{g.items.length}</span>
                </button>
                {isOpen && (
                  <div className="divide-y divide-[#1f1f1f]">
                    {g.items.map((it) => (
                      <div key={it.id} data-testid={`catalog-cat-row-${it.sku}`} className="flex items-center gap-3 px-4 py-2 hover:bg-[#0f0f0f]">
                        <span className="font-mono text-[#D4AF37] text-xs w-20 shrink-0">{it.sku}</span>
                        <span className="text-white text-sm flex-1 min-w-0 truncate">{it.name}</span>
                        <span className="hidden sm:flex gap-3 text-xs text-[#A3A3A3] shrink-0">{TIERS.map((t) => <span key={t} title={TIER_LABELS[t]}>{priceDisplay((it.pricing_profiles || {})[t])}</span>)}</span>
                        <button data-testid={`edit-catalog-${it.sku}`} onClick={() => openEdit(it)} className="text-[#A3A3A3] hover:text-white shrink-0"><Pencil className="w-4 h-4" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {viewMode === 'table' && (
      <div className="rounded-lg border border-[#262626] overflow-hidden" data-testid="catalog-list">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#141414] text-[#A3A3A3] text-xs uppercase tracking-wider">
              <tr>
                <th className="px-3 py-2 text-left">SKU</th>
                <th className="px-3 py-2 text-left">Service</th>
                <th className="px-3 py-2 text-left">Category</th>
                {TIERS.map((t) => <th key={t} className="px-3 py-2 text-right">{TIER_LABELS[t]}</th>)}
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="px-3 py-8 text-center text-[#A3A3A3]">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={9} className="px-3 py-8 text-center text-[#A3A3A3]">No services yet. Import from the Pricebook Import page, or add one.</td></tr>
              ) : filtered.map((it) => (
                <tr key={it.id} data-testid={`catalog-row-${it.sku}`} className="border-t border-[#1f1f1f]">
                  <td className="px-3 py-2 font-mono text-[#D4AF37] whitespace-nowrap">{it.sku}</td>
                  <td className="px-3 py-2 text-white">{it.name}</td>
                  <td className="px-3 py-2 text-[#A3A3A3]">{it.category}</td>
                  {TIERS.map((t) => <td key={t} className="px-3 py-2 text-right text-white whitespace-nowrap">{priceDisplay((it.pricing_profiles || {})[t])}</td>)}
                  <td className="px-3 py-2"><span className={`text-xs ${it.active ? 'text-emerald-300' : 'text-[#A3A3A3]'}`}>{it.active ? 'Active' : 'Inactive'}</span></td>
                  <td className="px-3 py-2"><button data-testid={`edit-catalog-${it.sku}`} onClick={() => openEdit(it)} className="text-[#A3A3A3] hover:text-white"><Pencil className="w-4 h-4" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* Redesigned item form */}
      {form && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="service-form-dialog">
          <div className="absolute inset-0 bg-black/70" onClick={() => setForm(null)} />
          <div className="relative bg-[#141414] border border-[#262626] rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-['Outfit'] text-xl font-bold">{form._new ? 'New Service' : `Edit ${form.sku}`}</h3>
              <button onClick={() => setForm(null)} className="text-[#A3A3A3] hover:text-white"><X className="w-5 h-5" /></button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-xs text-[#A3A3A3] mb-1">SKU *</label><input data-testid="form-sku" className="w-full bg-[#0A0A0A] border border-[#262626] rounded px-2 py-1.5 text-white" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} /></div>
              <div><label className="block text-xs text-[#A3A3A3] mb-1">Service Name *</label><input data-testid="form-name" className="w-full bg-[#0A0A0A] border border-[#262626] rounded px-2 py-1.5 text-white" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><label className="block text-xs text-[#A3A3A3] mb-1">Category *</label><input data-testid="form-category" className="w-full bg-[#0A0A0A] border border-[#262626] rounded px-2 py-1.5 text-white" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
              <div><label className="block text-xs text-[#A3A3A3] mb-1">Subcategory</label><input className="w-full bg-[#0A0A0A] border border-[#262626] rounded px-2 py-1.5 text-white" value={form.subcategory} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} /></div>
              <div><label className="block text-xs text-[#A3A3A3] mb-1">Department</label><input className="w-full bg-[#0A0A0A] border border-[#262626] rounded px-2 py-1.5 text-white" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} /></div>
              <div><label className="block text-xs text-[#A3A3A3] mb-1">Labor Minutes</label><input type="number" className="w-full bg-[#0A0A0A] border border-[#262626] rounded px-2 py-1.5 text-white" value={form.labor_minutes} onChange={(e) => setForm({ ...form, labor_minutes: e.target.value })} /></div>
              <div><label className="block text-xs text-[#A3A3A3] mb-1">Crew Size</label><input type="number" className="w-full bg-[#0A0A0A] border border-[#262626] rounded px-2 py-1.5 text-white" value={form.crew_size} onChange={(e) => setForm({ ...form, crew_size: e.target.value })} /></div>
              <div><label className="block text-xs text-[#A3A3A3] mb-1">Warranty</label><input className="w-full bg-[#0A0A0A] border border-[#262626] rounded px-2 py-1.5 text-white" value={form.warranty} onChange={(e) => setForm({ ...form, warranty: e.target.value })} /></div>
            </div>

            <label className="block text-xs text-[#A3A3A3] mb-1 mt-3">Customer-facing scope</label>
            <textarea className="w-full bg-[#0A0A0A] border border-[#262626] rounded px-2 py-1.5 text-white text-sm" rows={2} value={form.customer_scope} onChange={(e) => setForm({ ...form, customer_scope: e.target.value })} />
            <div className="grid grid-cols-2 gap-3 mt-2">
              <div><label className="block text-xs text-[#A3A3A3] mb-1">Technician instructions</label><textarea className="w-full bg-[#0A0A0A] border border-[#262626] rounded px-2 py-1.5 text-white text-sm" rows={2} value={form.tech_instructions} onChange={(e) => setForm({ ...form, tech_instructions: e.target.value })} /></div>
              <div><label className="block text-xs text-[#A3A3A3] mb-1">Exclusions</label><textarea className="w-full bg-[#0A0A0A] border border-[#262626] rounded px-2 py-1.5 text-white text-sm" rows={2} value={form.exclusions} onChange={(e) => setForm({ ...form, exclusions: e.target.value })} /></div>
            </div>

            {/* Independent pricing profiles */}
            <p className="text-white font-bold text-sm mt-4 mb-1">Pricing Profiles <span className="text-[#A3A3A3] font-normal text-xs">— four independent values, no formulas</span></p>
            <div className="space-y-2">
              {TIERS.map((t) => (
                <div key={t} className="rounded border border-[#262626] p-2" data-testid={`profile-${t}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white text-sm font-medium w-40 shrink-0">{TIER_LABELS[t]}</span>
                    <select data-testid={`form-method-${t}`} className="bg-[#0A0A0A] border border-[#262626] rounded px-1.5 py-1 text-white text-xs" value={form.pricing_profiles[t].pricing_method} onChange={(e) => setProfile(t, { pricing_method: e.target.value, price: e.target.value === 'flat' ? form.pricing_profiles[t].price : null })}>
                      {METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                    <input type="number" data-testid={`form-price-${t}`} disabled={form.pricing_profiles[t].pricing_method !== 'flat'} className="w-28 bg-[#0A0A0A] border border-[#262626] rounded px-2 py-1 text-white text-sm disabled:opacity-40" placeholder="0.00" value={form.pricing_profiles[t].price ?? ''} onChange={(e) => setProfile(t, { price: e.target.value === '' ? null : Number(e.target.value) })} />
                    <label className="text-xs text-[#A3A3A3] flex items-center gap-1"><input type="checkbox" checked={form.pricing_profiles[t].active} onChange={(e) => setProfile(t, { active: e.target.checked })} /> Active</label>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-1.5">
                    <input className="bg-[#0A0A0A] border border-[#262626] rounded px-2 py-1 text-white text-xs" placeholder="Customer-facing note" value={form.pricing_profiles[t].customer_note} onChange={(e) => setProfile(t, { customer_note: e.target.value })} />
                    <input className="bg-[#0A0A0A] border border-[#262626] rounded px-2 py-1 text-white text-xs" placeholder="Internal note" value={form.pricing_profiles[t].internal_note} onChange={(e) => setProfile(t, { internal_note: e.target.value })} />
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-3 mt-4">
              <label className="text-sm text-white flex items-center gap-1"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active</label>
              <label className="text-sm text-[#A3A3A3] flex items-center gap-1"><input type="checkbox" checked={form.archived} onChange={(e) => setForm({ ...form, archived: e.target.checked })} /> Archived</label>
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setForm(null)} className="text-sm px-3 py-1.5 rounded border border-[#3f3f3f] text-[#A3A3A3]">Cancel</button>
              <button data-testid="save-service-btn" onClick={save} disabled={saving} className="text-sm font-bold px-4 py-1.5 rounded bg-[#D4AF37] text-black disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceCatalog;
